import type { PrayerTimesData } from '../components/Home/PrayerTimes/types'

export const DIYANET_API_BASE_URL = 'https://ezanvakti.emushaf.net'

export type DiyanetLocationKind = 'countries' | 'cities' | 'districts'

export type DiyanetLocationOption = {
  id: string
  name: string
  nameEn?: string
}

export type DiyanetPrayerTime = {
  HicriTarihKisa?: string | null
  HicriTarihUzun?: string | null
  MiladiTarihKisa?: string | null
  MiladiTarihKisaIso8601?: string | null
  MiladiTarihUzunIso8601?: string | null
  Imsak?: string | null
  Gunes?: string | null
  Ogle?: string | null
  Ikindi?: string | null
  Aksam?: string | null
  Yatsi?: string | null
}

export type RawDiyanetLocation = {
  UlkeID?: string
  UlkeAdi?: string
  UlkeAdiEn?: string
  SehirID?: string
  SehirAdi?: string
  SehirAdiEn?: string
  IlceID?: string
  IlceAdi?: string
  IlceAdiEn?: string
}

const LOCATION_FIELDS: Record<
  DiyanetLocationKind,
  {
    id: keyof RawDiyanetLocation
    name: keyof RawDiyanetLocation
    nameEn: keyof RawDiyanetLocation
  }
> = {
  countries: { id: 'UlkeID', name: 'UlkeAdi', nameEn: 'UlkeAdiEn' },
  cities: { id: 'SehirID', name: 'SehirAdi', nameEn: 'SehirAdiEn' },
  districts: { id: 'IlceID', name: 'IlceAdi', nameEn: 'IlceAdiEn' },
}

export const normalizeDiyanetLocations = (
  kind: DiyanetLocationKind,
  locations: RawDiyanetLocation[],
): DiyanetLocationOption[] => {
  const fields = LOCATION_FIELDS[kind]

  return locations.flatMap((location) => {
    const id = location[fields.id]
    const name = location[fields.name]
    const nameEn = location[fields.nameEn]

    if (!id || !name) return []

    return [{ id, name, ...(nameEn ? { nameEn } : {}) }]
  })
}

export const normalizeDiyanetSearchText = (text: string): string =>
  text
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i')

const normalizeDate = (prayerTime: DiyanetPrayerTime): string | null => {
  const longIsoDate = prayerTime.MiladiTarihUzunIso8601?.match(/^\d{4}-\d{2}-\d{2}/)?.[0]
  if (longIsoDate) return longIsoDate

  const shortDate = prayerTime.MiladiTarihKisaIso8601 || prayerTime.MiladiTarihKisa
  const match = shortDate?.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
  return match ? `${match[3]}-${match[2]}-${match[1]}` : null
}

const normalizeTime = (time?: string | null): string | null => {
  const match = time?.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return null

  return `${match[1].padStart(2, '0')}:${match[2]}`
}

export const normalizeDiyanetPrayerTimes = (
  districtId: string,
  prayerTimes: DiyanetPrayerTime[],
): PrayerTimesData => ({
  source: 'diyanet',
  districtId,
  data: prayerTimes.flatMap((prayerTime) => {
    const date = normalizeDate(prayerTime)
    if (!date) return []

    return [
      {
        date,
        hijriDate: prayerTime.HicriTarihUzun || prayerTime.HicriTarihKisa || undefined,
        times: {
          Imsak: normalizeTime(prayerTime.Imsak),
          Gunes: normalizeTime(prayerTime.Gunes),
          Ogle: normalizeTime(prayerTime.Ogle),
          Ikindi: normalizeTime(prayerTime.Ikindi),
          Aksam: normalizeTime(prayerTime.Aksam),
          Yatsi: normalizeTime(prayerTime.Yatsi),
        },
      },
    ]
  }),
})
